import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
    AccordionWithChildren,
    EProviderName,
    IInvoicingPrice,
    IWebshopFeaturesDetails,
    MixedMerchantDetails,
    PageContainer,
    PageWrapper,
    TEnv,
    TOnboardingStatus,
    TPlatformValue,
    TSwiipePlan,
    TSwiipeService,
    WebshopDetails,
    canEditKycFlow,
    getActivePayFac,
    getAvailablePlatformsForPaymentType,
    getTermsForPayFacs,
    getText,
    getTextWithInvoicingPrices,
    hasValue,
    isServiceAvailableForMerchantCountry,
    kycSelectors,
    mapMerchantOnboardingServicesSubFormToServices,
    merchantSelectors,
    providerSelectors,
    sharedConfigurationSelectors,
    useConfiguredPaymentProviders,
    useProviderForPaymentType,
    useSelector,
} from "swiipe.portal.shared"
import Plugins, { IPlugin } from "../../components/merchant/Plugin"
import WebshopSelect from "../../components/merchant/WebshopSelect"
import AnywhereMobileLogo from "../../images/swFeatureServices/anywhere-mobile-logo.svg"
import PlusSellLogo from "../../images/swFeatureServices/plussell-logo.png"
import ReturnSellLogo from "../../images/swFeatureServices/returnsell-logo.png"
import WinBackLogo from "../../images/swFeatureServices/winback-logo.png"
import { getClearhausPluginStatus, getClearhausPluginStatusType } from "../../services/clearhausService"
import { getImage } from "../../services/imageLanguageService"
import { useMerchantPreOnboardingDetails } from "../../services/merchantOfferService"
import { navigationService } from "../../services/navigationService"
import { isWebshopOnboardedForFlow } from "../../services/onboardingService"
import { getProvider, getProviderStatus } from "../../services/paymentService"
import { filterPluginsByPlatform, useCalculatedPluginsDependencies } from "../../services/pluginService"
import { getPspPriceHtml } from "../../services/pspService"
import { getBestShopifyAppUrls } from "../../services/shopifyService"
import { getTermsForSwiipeService } from "../../services/swiipeServicesService"
import { useRequiredUserRelations } from "../../services/userRelationService"
import { IGatewayDto } from "../../store/reducers/gatewayReducer"
import { legalSelectors } from "../../store/reducers/legalReducer"
import { IMerchantPreOnboardingDetails } from "../../store/reducers/merchantPreOnboardingSlice"
import { IWebshopProviderConfig } from "../../store/reducers/merchantServicesReducer"
import { shopifySelectors } from "../../store/reducers/shopifySlice"
import { userRelationSelectors } from "../../store/reducers/userRelationReducer"
import { StoreState } from "../../store/StoreState"
import { getMerchantPreOnboardingDetailsThunk } from "../../store/thunks/merchantPreOnboardingThunks"
import { ensureShopifyAppUrlsThunks } from "../../store/thunks/shopifyThunks"
import { ensureTermsStatusThunk } from "../../store/thunks/termsThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import { IAcquirerPluginStatus } from "../../type/acquirer/IAcquirerPluginStatus"
import { IClearhausApplicationOnboardingState } from "../../type/clearhaus/IClearhausApplicationOnboardingState"
import "./MerchantPluginsPage.scss"

const getPluginData = (webshopDetails: WebshopDetails | undefined, merchantCountry: string, env: TEnv): IPlugin[] => {
    const t = getText
    const swiipeWoocommerce: IPlugin = {
        id: undefined,
        platforms: ["woocommerce"] as TPlatformValue[],
        price: getText("plugins.freePrice"),
        isPlugin: true,
        isFetched: true,
        logo: getImage("checkoutLogo"),
        logoWidth: 175,
        title: "",
        installationLink: { url: "https://docs.ecom.aera.id", type: "docs" },
        buttonLink: "https://plugin-repo.ecom.aera.id/woocommerceplugins/aera-general-int.zip",
        pluginDescription: t("plugins.aeraDescription"),
        legalTextType: ["TermsMerchant"],
        legalTextLinkText: t("plugins.here"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const swiipeMagento: IPlugin = {
        id: undefined,
        platforms: ["magento"] as TPlatformValue[],
        price: t("plugins.freePrice"),
        isPlugin: true,
        isFetched: true,
        logo: getImage("checkoutLogo"),
        logoWidth: 175,
        title: "",
        installationLink: { url: "https://docs.ecom.aera.id", type: "docs" },
        buttonLink: "https://plugin-repo.ecom.aera.id/magento1plugins/swiipe-checkout-dk-latest.zip",
        pluginDescription: t("plugins.aeraDescriptionMagento"),
        legalTextType: ["TermsMerchant"],
        legalTextLinkText: t("plugins.here"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const swiipeMagento2: IPlugin = {
        id: undefined,
        platforms: ["magento2"] as TPlatformValue[],
        price: t("plugins.freePrice"),
        isPlugin: true,
        isFetched: true,
        logo: getImage("checkoutLogo"),
        logoWidth: 205,
        title: "",
        installationLink: { url: "https://docs.ecom.aera.id", type: "docs" },
        buttonLink: "https://plugin-repo.ecom.aera.id/magento2plugins/composer-dk/composer-satis/index.html",
        pluginDescription: t("plugins.aeraDescriptionMagento"),
        legalTextType: ["TermsMerchant"],
        legalTextLinkText: t("plugins.here"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const swiipePrestashop: IPlugin = {
        id: undefined,
        platforms: ["prestashop"] as TPlatformValue[],
        price: t("plugins.freePrice"),
        isPlugin: true,
        isFetched: true,
        logo: getImage("checkoutLogo"),
        logoWidth: 175,
        title: "",
        installationLink: { url: "https://docs.ecom.aera.id", type: "docs" },
        buttonLink: "https://plugin-repo.ecom.aera.id/prestashopplugins/aera-general-int.zip",
        pluginDescription: t("plugins.aeraDescription"),
        legalTextType: ["TermsMerchant"],
        legalTextLinkText: t("plugins.here"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    if (!webshopDetails || !merchantCountry) {
        return []
    }

    return filterPluginsByPlatform([swiipeWoocommerce, swiipeMagento, swiipeMagento2, swiipePrestashop], webshopDetails.platform)
}

const useServiceData = (
    webshop: {
        id: string
        details: WebshopDetails | undefined
        features: IWebshopFeaturesDetails
        providers: IWebshopProviderConfig | undefined
    },
    merchant: {
        id: string
        isDeletionPending: boolean
        country: string
        gateways: IGatewayDto[] | undefined
    },
    global: {
        env: TEnv
        invoicingPrices: IInvoicingPrice[] | undefined
        swiipePlan: TSwiipePlan | undefined
    },
    clearhaus: {
        applicationState: IClearhausApplicationOnboardingState | undefined
    },
    sandbox: {
        getSandboxOnboardingAction: (providerName: EProviderName) => (() => Promise<void>) | undefined
    }
): IPlugin[] => {
    const t = getText
    const swiipePlanSelected = !!global.swiipePlan

    const kycFlows = useSelector((state: StoreState) => kycSelectors.flows(state, merchant.id))
    const providerConfigs = useSelector((state: StoreState) => providerSelectors.configs(state, merchant.id))

    const isOnboardedForCardPayments =
        swiipePlanSelected &&
        isWebshopOnboardedForFlow(webshop.id, merchant.country, webshop.providers, "cards", kycFlows, providerConfigs ?? [], true)
    const activePayFac = getActivePayFac(merchant.country)

    const getAcquirerDependentStatus = (status?: TOnboardingStatus): IAcquirerPluginStatus => {
        if (activePayFac === "Clearhaus") {
            return getClearhausPluginStatus(
                getClearhausPluginStatusType(
                    swiipePlanSelected,
                    merchant.isDeletionPending,
                    global.env,
                    webshop.providers,
                    status,
                    clearhaus.applicationState
                ),
                webshop.id
            )
        }
        const isProvidersFetched = !!webshop.providers?.gatewayProviders
        const flow = kycFlows?.find(
            (f) =>
                f.flowType === "DigitealApi" &&
                f.organizationId === merchant.id &&
                (!f.webshopId || f.webshopId === webshop.id) &&
                f.state
        )

        const hasData = isProvidersFetched && !!merchant.gateways && !!kycFlows
        const missingStatus = isProvidersFetched && !status

        if (global.env === "Sandbox") {
            return {
                isFetched: hasData,
                isOnboarded: status === "InProgress" || status === "Ready",
            }
        }

        return {
            isFetched: hasData,
            isOnboarded: hasData && !missingStatus && !merchant.isDeletionPending && swiipePlanSelected && !!flow,
            isSavedNoStagesSubmitted: flow?.state === "Initiated" && flow?.hasUnsubmittedChanges,
            showLimitedAccessHint: status === "Ready" && flow?.state === "Partial",
            isAwaitingApproval:
                flow?.subState === "AwaitingConfirmationForPartial" ||
                flow?.subState === "AwaitingConfirmationForComplete" ||
                flow?.subState === "AwaitingCompanyTransferForPartial" ||
                flow?.subState === "AwaitingCompanyTransferForComplete" ||
                ((flow?.state === "Partial" || flow?.state === "Complete") && status === "InProgress"),
            canEditApplication: canEditKycFlow(flow),
            isNeedMoreInfo: flow?.subState === "ChangesRequiredForPartial" || flow?.subState === "ChangesRequiredForComplete",
            isChangesRequested: flow?.subState === "ChangesRequiredForPartial" || flow?.subState === "ChangesRequiredForComplete",
            canViewApplicationData: false,
            buttonLink: undefined,
        } as any
    }

    const ccOnboardingStatus = getAcquirerDependentStatus(
        activePayFac === "Clearhaus"
            ? getProviderStatus(webshop.providers?.gatewayProviders, "CreditCard", "Clearhaus")
            : getProviderStatus(webshop.providers?.gatewayProviders, "CreditCard", "Digiteal")
    )
    const cardPaymentPrice =
        (global.invoicingPrices?.find((p) => p.priceType === "CardPaymentTransactionShared")?.amount ?? 0) > 0
            ? "plugins.cardPaymentPrice"
            : "plugins.acquirerPrice"

    const swiipePayments: IPlugin = {
        id: "CreditCard",
        platforms: getAvailablePlatformsForPaymentType("CreditCard"),
        price: getTextWithInvoicingPrices(cardPaymentPrice, global.invoicingPrices, {
            cardPaymentTx: { type: "CardPaymentTransactionShared" },
            acquirerTxPct: { category: "AcquirerTransactionPct" },
        }),
        priceSubText: t("plugins.plusPspFees"),
        editNode: swiipePlanSelected && !merchant.isDeletionPending && (
            <>
                <div>
                    {t("plugins.editNode.pspPlan")} <br />
                    {t("plugins.editNode.acquiring")}: {activePayFac === "Clearhaus" ? "Clearhaus" : "Digiteal"} <br />
                    <br />
                    <span onClick={() => navigationService.navigate("/psp_acquiring_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            </>
        ),
        logo: getImage("cardPaymentsLogo"),
        isFetched: ccOnboardingStatus.isFetched,
        logoWidth: 180,
        logoTopMargin: 15,
        title: t("plugins.aeraPaymentsTitle"),
        buttonFunc: activePayFac === "Clearhaus" ? sandbox.getSandboxOnboardingAction("Clearhaus") : undefined,
        pluginDescription: t("plugins.aeraPaymentsOnboardingDescription", {
            acquiringDescLong: t(
                activePayFac === "Clearhaus"
                    ? "plugins.acquiringDescriptionLongClearhaus"
                    : "plugins.acquiringDescriptionLongDigiteal"
            ),
        }),
        textUnderDescription:
            activePayFac === "Clearhaus"
                ? "plugins.acquiringDescriptionShortClearhaus"
                : "plugins.acquiringDescriptionShortDigiteal",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        legalTextType: ["TermsPSP", ...getTermsForPayFacs([activePayFac])],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.psp"), t("serviceNames.acquirer")],
        cardTypes: ["Visa", "MasterCard"],
        installationLink: { url: "https://apps.shopify.com/aera-card-payments", type: "shopify" },

        isOnboarded: ccOnboardingStatus.isOnboarded,
        onboardedText:
            (ccOnboardingStatus.isNeedMoreInfo && t("plugins.needMoreInfo")) ||
            (ccOnboardingStatus.isSavedNoStagesSubmitted && t("plugins.applicationStarted")) ||
            (ccOnboardingStatus.isAwaitingApproval && t("plugins.waitingapproval")) ||
            t("plugins.onboarded"),
        onboardedTextItalic: ccOnboardingStatus.isAwaitingApproval,
        onboardedSecondaryText: (ccOnboardingStatus.isChangesRequested && t("plugins.changesRequested")) || undefined,
        onboardedSecondaryTextItalic: true,
        activateBtnText: (ccOnboardingStatus.canEditApplication && t("plugins.completeApplication")) || undefined,
        hintBtnSection:
            (ccOnboardingStatus.showLimitedAccessHint && {
                title: t("plugins.clearhausLimitedAccess.title"),
                hint: t("plugins.clearhausLimitedAccess.hint"),
            }) ||
            undefined,
        canEdit: ccOnboardingStatus.canEditApplication || ccOnboardingStatus.canViewApplicationData || false,
        editBtnText:
            (ccOnboardingStatus.canEditApplication && t("plugins.completeApplication")) ||
            (ccOnboardingStatus.canViewApplicationData && t("plugins.yourInfoAtClearhaus")) ||
            undefined,
        editBtnAsText: ccOnboardingStatus.canViewApplicationData || false,
        buttonLink: ccOnboardingStatus.buttonLink || "/swiipepayments_onboard",
        actionButtonPurple: ccOnboardingStatus.canEditApplication,
    }

    const dankortCcStatus = getProviderStatus(webshop.providers?.gatewayProviders, "CreditCard", "DankortEpay")
    const dankort: IPlugin = {
        id: "Dankort",
        platforms: getAvailablePlatformsForPaymentType("CreditCard", "DankortEpay"),
        price: getPspPriceHtml("transaction", global.invoicingPrices),
        editNode:
            swiipePlanSelected && !!dankortCcStatus && dankortCcStatus !== "ActionRequired" && !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/dankort_details")}>{t("plugins.editNode.seeDetails")}</span>
                </div>
            ) : undefined,
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("cardPaymentsLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded:
            swiipePlanSelected && !!dankortCcStatus && dankortCcStatus !== "ActionRequired" && !merchant.isDeletionPending,
        logoWidth: 180,
        logoTopMargin: 15,
        title: "",
        buttonLink: "/dankort_onboard",
        buttonFunc: sandbox.getSandboxOnboardingAction("DankortEpay"),
        pluginDescription: t("plugins.dankortDescription"),
        legalTextType: ["TermsDankort"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.dankort")],
        onboardedText: swiipePlanSelected && dankortCcStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !swiipePlanSelected || dankortCcStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        cardTypes: ["Dankort"],
        canEdit: false,
    }

    const forbrugsforeningenStatus = getProviderStatus(
        webshop.providers?.gatewayProviders,
        "CreditCard",
        "ForbrugsforeningenEpay"
    )
    const forbrugsforeningen: IPlugin = {
        id: "Forbrugsforeningen",
        platforms: getAvailablePlatformsForPaymentType("CreditCard", "ForbrugsforeningenEpay"),
        price: getPspPriceHtml("transaction", global.invoicingPrices),
        editNode:
            swiipePlanSelected &&
            !!forbrugsforeningenStatus &&
            forbrugsforeningenStatus !== "ActionRequired" &&
            !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/forbrugsforeningen_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            ) : undefined,
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("cardPaymentsLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded:
            swiipePlanSelected &&
            !!forbrugsforeningenStatus &&
            forbrugsforeningenStatus !== "ActionRequired" &&
            !merchant.isDeletionPending,
        logoWidth: 180,
        logoTopMargin: 15,
        title: "",
        buttonLink: "/forbrugsforeningen_onboard",
        buttonFunc: sandbox.getSandboxOnboardingAction("ForbrugsforeningenEpay"),
        pluginDescription: t("plugins.forbrugsforeningenDescription"),
        legalTextType: ["TermsForbrugsforeningen"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.forbrugsforeningen")],
        onboardedText:
            swiipePlanSelected && forbrugsforeningenStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !swiipePlanSelected || forbrugsforeningenStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        cardTypes: ["Forbrugsforeningen"],
        canEdit: false,
    }

    const mpoStatus = getProviderStatus(webshop.providers?.gatewayProviders, "MobilePayOnline", "MobilePayOnline")
    const clearhausMpoStatus = getProviderStatus(webshop.providers?.gatewayProviders, "MobilePayOnline", "Clearhaus")
    const mobilePayOnline: IPlugin = {
        id: "MobilePayOnline",
        platforms: getAvailablePlatformsForPaymentType("MobilePayOnline"),
        price: getTextWithInvoicingPrices("plugins.mobilepayOnlinePrice", global.invoicingPrices, {
            mpoMonthly: { type: "MobilePayOnlineMonthlyShared" },
            mpoTx: { type: "MobilePayOnlineTransactionStep1Shared" },
        }),
        editNode:
            swiipePlanSelected && !!mpoStatus && mpoStatus !== "ActionRequired" && !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/mpo_details")}>{t("plugins.editNode.seeDetails")}</span>
                </div>
            ) : undefined,
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("mobilepayLogo"),
        isFetched: webshop.providers ? true : false,
        installationLink: { url: "https://apps.shopify.com/aera-mobilepay", type: "shopify" },
        isOnboarded:
            swiipePlanSelected &&
            !!mpoStatus &&
            mpoStatus != "ActionRequired" &&
            !!clearhausMpoStatus &&
            !merchant.isDeletionPending,
        logoWidth: 185,
        title: "",
        buttonLink: "/mobilepayonline_onboarding",
        buttonFunc: sandbox.getSandboxOnboardingAction("MobilePayOnline"),
        pluginDescription: t("plugins.mobilepayOnlineDescription"),
        legalTextType: ["TermsMobilePayOnline"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.mpo")],
        onboardedText:
            swiipePlanSelected && clearhausMpoStatus === "Ready" && mpoStatus === "Ready"
                ? t("plugins.onboarded")
                : t("plugins.waitingapproval"),
        onboardedTextItalic: !swiipePlanSelected || clearhausMpoStatus !== "Ready" || mpoStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const swishStatus = getProviderStatus(webshop.providers?.gatewayProviders, "Swish", "Swish")
    const swish: IPlugin = {
        id: "Swish",
        platforms: getAvailablePlatformsForPaymentType("Swish"),
        editNode:
            swiipePlanSelected && !!swishStatus && swishStatus !== "ActionRequired" ? (
                <div>
                    <span onClick={() => navigationService.navigate("/swish_details")}>{t("plugins.editNode.seeDetails")}</span>
                </div>
            ) : undefined,
        price: getTextWithInvoicingPrices("plugins.swishPrice", global.invoicingPrices, {
            swishTx: { type: "SwishTransactionShared" },
        }),
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("swishLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: swiipePlanSelected && !!swishStatus && swishStatus != "ActionRequired",
        logoWidth: 135,
        title: "",
        buttonLink: "/swish_onboarding",
        buttonFunc: sandbox.getSandboxOnboardingAction("Swish"),
        pluginDescription: t("plugins.swishDescription"),
        legalTextType: ["TermsSwish"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.swish")],
        onboardedText: swiipePlanSelected && swishStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !swiipePlanSelected && swishStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const vippsConfiguredProvider = useProviderForPaymentType("Vipps", webshop.id)
    const vippsOnboardingStatus = getProviderStatus(webshop.providers?.gatewayProviders, "Vipps", vippsConfiguredProvider)
    const vippsEnabled =
        !merchant.isDeletionPending && swiipePlanSelected && !!vippsOnboardingStatus && vippsOnboardingStatus !== "ActionRequired"

    const vipps: IPlugin = {
        id: "Vipps",
        platforms: getAvailablePlatformsForPaymentType("Vipps"),
        editNode: vippsEnabled ? (
            <div>
                <span onClick={() => navigationService.navigate("/vipps_details")}>{t("plugins.editNode.seeDetails")}</span>
            </div>
        ) : undefined,
        price: getTextWithInvoicingPrices("plugins.vippsPrice", global.invoicingPrices, {
            vippsTx: { type: "VippsTransactionShared" },
        }),
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("vippsLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: vippsEnabled,
        logoWidth: 135,
        title: "",
        buttonLink: "/vipps_onboarding",
        buttonFunc: vippsConfiguredProvider == "PaymentIQ" ? sandbox.getSandboxOnboardingAction("VippsMobilePay") : undefined,
        pluginDescription: t("plugins.vippsDescription"),
        legalTextType: [vippsConfiguredProvider === "VippsMobilePay" ? "TermsVippsEpayment" : "TermsVippsPsp"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.vipps")],
        onboardedText: vippsEnabled && vippsOnboardingStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !vippsEnabled || vippsOnboardingStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const termsTechOnboardingStatus = getProviderStatus(webshop.providers?.gatewayProviders, "TermsTech", "Area42")
    const termsTechEnabled =
        !merchant.isDeletionPending &&
        swiipePlanSelected &&
        !!termsTechOnboardingStatus &&
        termsTechOnboardingStatus !== "ActionRequired"

    const termsTech: IPlugin = {
        id: "TermsTech",
        platforms: getAvailablePlatformsForPaymentType("TermsTech"),
        editNode: termsTechEnabled ? (
            <div>
                <span onClick={() => navigationService.navigate("/termstech_details")}>{t("plugins.editNode.seeDetails")}</span>
            </div>
        ) : undefined,
        price: t("plugins.pspFees"),
        logo: getImage("termsTechLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: termsTechEnabled,
        logoWidth: 180,
        title: "",
        buttonLink: "/termstech_onboarding",
        buttonFunc: sandbox.getSandboxOnboardingAction("Area42"),
        pluginDescription: t("plugins.termsTechDescription", {
            linkStart: `<a href='https://area42.tech/termstech-payment-terms-bnpl/' target='_blank' rel="noopener noreferrer">`,
            linkEnd: "</a>",
        }),
        legalTextType: ["TermsTermsTech"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.termsTech")],
        onboardedText:
            termsTechEnabled && termsTechOnboardingStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !termsTechEnabled || termsTechOnboardingStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const applePayStatus = getProviderStatus(webshop.providers?.gatewayProviders, "ApplePay", "Clearhaus")
    const applePay: IPlugin = {
        id: "ApplePay",
        platforms: getAvailablePlatformsForPaymentType("ApplePay"),
        editNode:
            swiipePlanSelected && !!applePayStatus && applePayStatus !== "ActionRequired" ? (
                <div>
                    <span onClick={() => navigationService.navigate("/applepay_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            ) : undefined,
        price: getTextWithInvoicingPrices("plugins.applePayPrice", global.invoicingPrices, {
            applePayTx: { type: "ApplePayTransactionShared" },
        }),
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("applePayLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: swiipePlanSelected && !!applePayStatus && applePayStatus != "ActionRequired",
        logoWidth: 135,
        title: "",
        buttonLink: "/applepay_onboarding",
        buttonFunc: sandbox.getSandboxOnboardingAction("ApplePay"),
        pluginDescription: t("plugins.applePayDescription"),
        legalTextType: ["TermsApplePay"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.applepay")],
        onboardedText: swiipePlanSelected && applePayStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !swiipePlanSelected || applePayStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const klarnaStatus = getProviderStatus(webshop.providers?.gatewayProviders, "KlarnaPayLater", "Klarna")
    const klarna: IPlugin = {
        id: "KlarnaPayLater",
        platforms: getAvailablePlatformsForPaymentType("KlarnaPayLater"),
        editNode:
            swiipePlanSelected && !!klarnaStatus && klarnaStatus !== "ActionRequired" ? (
                <div>
                    <span onClick={() => navigationService.navigate("/klarna_details")}>{t("plugins.editNode.seeDetails")}</span>
                </div>
            ) : undefined,
        price: getTextWithInvoicingPrices("plugins.klarnaPrice", global.invoicingPrices, {
            klarnaTx: { type: "KlarnaTransactionShared" },
        }),
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("klarnaLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: swiipePlanSelected && !!klarnaStatus && klarnaStatus != "ActionRequired",
        logoWidth: 135,
        title: "",
        buttonLink: "/klarna_onboarding",
        buttonFunc: sandbox.getSandboxOnboardingAction("Klarna"),
        pluginDescription: t("plugins.klarnaDescription"),
        legalTextType: ["TermsKlarna"],
        legalTextLinkText: t("plugins.clickhere"),
        legalTextLinkOnboarded: [t("serviceNames.klarna")],
        onboardedText: swiipePlanSelected && klarnaStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: swiipePlanSelected && klarnaStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const resursStatus = getProviderStatus(
        webshop.providers?.gatewayProviders,
        ["ResursInvoice", "ResursPartPayment", "ResursRevolvingCredit"],
        "Resurs"
    )
    const resurs: IPlugin = {
        id: "ResursInvoice",
        platforms: getAvailablePlatformsForPaymentType("ResursInvoice"),
        editNode:
            swiipePlanSelected && !!resursStatus && resursStatus !== "ActionRequired" ? (
                <div>
                    <span onClick={() => navigationService.navigate("/resurs_details")}>{t("plugins.editNode.seeDetails")}</span>
                </div>
            ) : undefined,
        price: getTextWithInvoicingPrices("plugins.resursPrice", global.invoicingPrices, {
            resursInvoicePct: { type: "ResursInvoicePctTransactionShared" },
            resursPartPaymentPct: { type: "ResursPartPaymentPctTransactionShared" },
            resursRevolvingCreditPct: { type: "ResursRevolvingCreditPctTransactionShared" },
        }),
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("resursLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: swiipePlanSelected && !!resursStatus && resursStatus != "ActionRequired",
        logoWidth: 135,
        title: "",
        buttonLink: "/resurs_onboarding",
        buttonFunc: sandbox.getSandboxOnboardingAction("Resurs"),
        pluginDescription: t("plugins.resursDescription"),
        textUnderDescription: "plugins.resursTextUnderDescription",
        legalTextType: ["TermsResurs"],
        legalTextLinkText: t("plugins.clickhere"),
        legalTextLinkOnboarded: [t("serviceNames.resursPayments")],
        onboardedText: swiipePlanSelected && resursStatus === "Ready" ? t("plugins.onboarded") : t("plugins.waitingapproval"),
        onboardedTextItalic: !swiipePlanSelected && resursStatus !== "Ready",
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        removeBtnMargin: true,
    }

    const a2aProvider = getProvider("AccountToAccount", "AccountToAccount", webshop.providers?.gatewayProviders)
    // const a2aGateway = merchant.gateways?.find((gateway) => gateway.paymentType == "AccountToAccount")
    const accountToAccount: IPlugin = {
        id: "AccountToAccount",
        platforms: getAvailablePlatformsForPaymentType("AccountToAccount"),
        price: getTextWithInvoicingPrices("plugins.a2aPrice", global.invoicingPrices, {
            a2aMonthly: { type: "A2aMonthlyShared" },
            a2aMinTx: { type: "A2aMinTransactionShared" },
        }),
        logo: getImage("a2aLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: swiipePlanSelected && a2aProvider?.enabled,
        logoWidth: 190,
        logoTopMargin: 15,
        title: "",
        buttonLink: "/account_to_account_onboarding",
        editNode:
            swiipePlanSelected && !!a2aProvider && a2aProvider?.enabled && !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/account_to_account_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            ) : undefined,
        pluginDescription: t("plugins.a2aDescription"),
        legalTextType: ["TermsA2a"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.a2a")],
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
        showAiiaLogo: true,
    }

    const winBackEnabled = webshop.features && webshop.features["Winback"] ? webshop.features["Winback"].enabled : false
    const winBack: IPlugin = {
        id: "Winback",
        platforms: ["woocommerce", "magento2", "magento", "shopify"] as TPlatformValue[],
        price: getTextWithInvoicingPrices("plugins.winbackPrice", global.invoicingPrices, {
            winbackTxPrice: { type: "SwiipeWinbackPctBasic" },
        }),
        logo: WinBackLogo,
        editNode:
            winBackEnabled && !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/winback_edit")}>{t("plugins.editNode.seeDetails")}</span>
                </div>
            ) : undefined,
        isFetched: !!webshop.features,
        installationLink: { url: "https://apps.shopify.com/aera-win-back", type: "shopify" },
        isOnboarded: winBackEnabled && !merchant.isDeletionPending,
        logoWidth: 165,
        title: "",
        buttonLink: winBackEnabled ? "/winback_edit" : "/winback_onboard",
        pluginDescription: t("plugins.winbackDescription"),
        legalTextType: ["TermsWinBack"],
        legalTextLinkOnboarded: [t("serviceNames.winback")],
        legalTextLinkText: t("plugins.here"),
        onboardedText: t("plugins.active"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: true && !merchant.isDeletionPending,
    }

    const plusSellEnabled = webshop.features && webshop.features["PlusSell"] ? webshop.features["PlusSell"].enabled : false
    const plusSell: IPlugin = {
        id: "PlusSell",
        platforms: ["woocommerce", "magento2", "magento"] as TPlatformValue[],
        price: getTextWithInvoicingPrices("plugins.plusSellPrice", global.invoicingPrices, {
            plusSellTx: { category: "SwiipePlusSellPct" },
        }),
        logo: PlusSellLogo,
        editNode:
            plusSellEnabled && !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/plussell_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            ) : undefined,
        isFetched: !!webshop.features,
        isOnboarded: plusSellEnabled && !merchant.isDeletionPending,
        logoWidth: 160,
        title: "",
        buttonLink: plusSellEnabled ? "/plussell_details" : "/plussell_onboard",
        pluginDescription: t("plugins.plusSellDescription"),
        legalTextType: ["TermsPlusSell"],
        legalTextLinkOnboarded: [t("serviceNames.plusSell")],
        legalTextLinkText: t("plugins.here"),
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const anywhereMobileEnabled =
        webshop.features && webshop.features["AnywhereMobile"] ? webshop.features["AnywhereMobile"].enabled : false
    const anywhereMobile: IPlugin = {
        id: "AnywhereMobile",
        platforms: ["woocommerce", "magento2"] as TPlatformValue[], // TODO: add magento to the list before new release of magento
        price: getTextWithInvoicingPrices("plugins.anywhereMobilePrice", global.invoicingPrices, {
            anywhereMobileMonthly: { category: "SwiipeAnywhereMobileMonthly" },
            anywhereMobileExtraDomainMonthly: {
                category: "SwiipeAnywhereMobileExtraWebshopMonthly",
            },
        }),
        logo: AnywhereMobileLogo,
        editNode:
            (anywhereMobileEnabled && !merchant.isDeletionPending && (
                <div>
                    <span onClick={() => navigationService.navigate("/anywhere_mobile_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            )) ||
            undefined,
        isFetched: !!webshop.features,
        isOnboarded: anywhereMobileEnabled && !merchant.isDeletionPending,
        logoWidth: 220,
        title: "",
        buttonLink: anywhereMobileEnabled ? "/anywhere_mobile_details" : "/anywhere_mobile_onboard",
        pluginDescription: t("plugins.anywhereMobileDescription"),
        legalTextType: ["TermsAnywhereMobile"],
        legalTextLinkOnboarded: [t("serviceNames.anywhereMobile")],
        legalTextLinkText: t("plugins.here"),
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const returnSellEnabled = webshop.features && webshop.features["ReturnSell"] ? webshop.features["ReturnSell"].enabled : false
    const returnSell: IPlugin = {
        id: "ReturnSell",
        platforms: ["woocommerce", "magento2", "magento"] as TPlatformValue[],
        price: getTextWithInvoicingPrices("plugins.returnSellPrice", global.invoicingPrices, {
            returnSellMonthly: { category: "SwiipeReturnSellMonthly" },
            returnSellTx: { category: "SwiipeReturnSellTransactionPct" },
            returnSellExtraMonthly: { category: "SwiipeReturnSellExtraWebshopMonthly" },
        }),
        logo: ReturnSellLogo,
        editNode:
            returnSellEnabled && !merchant.isDeletionPending ? (
                <div>
                    <span onClick={() => navigationService.navigate("/returnsell_details")}>
                        {t("plugins.editNode.seeDetails")}
                    </span>
                </div>
            ) : undefined,
        isFetched: !!webshop.features,
        isOnboarded: returnSellEnabled && !merchant.isDeletionPending,
        logoWidth: 220,
        title: "",
        buttonLink: returnSellEnabled ? "/returnsell_details" : "/returnsell_onboard",
        pluginDescription: t("plugins.returnSellDescription"),
        legalTextType: ["TermsReturnSell"],
        legalTextLinkOnboarded: [t("serviceNames.returnSell")],
        legalTextLinkText: t("plugins.here"),
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    if (!webshop.details || !merchant.country) {
        return []
    }

    const viabillOnboardingStatus = getProviderStatus(webshop.providers?.gatewayProviders, "Viabill", "Viabill")
    const viabillEnabled =
        !merchant.isDeletionPending &&
        swiipePlanSelected &&
        !!viabillOnboardingStatus &&
        viabillOnboardingStatus !== "ActionRequired"

    const viabill: IPlugin = {
        id: "Viabill",
        platforms: getAvailablePlatformsForPaymentType("Viabill"),
        price: getPspPriceHtml("transaction", global.invoicingPrices),
        editNode: viabillEnabled ? (
            <div>
                <span onClick={() => navigationService.navigate("/viabill_edit")}>{t("plugins.editNode.seeDetails")}</span>
            </div>
        ) : undefined,
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("viabillLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: viabillEnabled,
        logoWidth: 135,
        title: "",
        buttonLink: "/viabill_onboard",
        pluginDescription: t("plugins.viabillDescription"),
        legalTextType: ["TermsViaBill"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.viabill")],
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const anydayOnboardingStatus = getProviderStatus(webshop.providers?.gatewayProviders, "Anyday", "Anyday")
    const anydayEnabled =
        !merchant.isDeletionPending &&
        swiipePlanSelected &&
        !!anydayOnboardingStatus &&
        anydayOnboardingStatus !== "ActionRequired"

    const anyday: IPlugin = {
        id: "Anyday",
        platforms: getAvailablePlatformsForPaymentType("Anyday"),
        price: getPspPriceHtml("transaction", global.invoicingPrices),
        editNode: anydayEnabled ? (
            <div>
                <span onClick={() => navigationService.navigate("/anyday_edit")}>{t("plugins.editNode.seeDetails")}</span>
            </div>
        ) : undefined,
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("anydayLogo"),
        isFetched: webshop.providers ? true : false,
        isOnboarded: anydayEnabled,
        logoWidth: 135,
        title: "",
        buttonLink: "/anyday_onboard",
        pluginDescription: t("plugins.anydayDescription"),
        legalTextType: ["TermsAnyday"],
        legalTextLinkText: t("plugins.here"),
        legalTextLinkOnboarded: [t("serviceNames.anyday")],
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const bancontactOnboardingStatus = getProviderStatus(webshop.providers?.gatewayProviders, "Bancontact", "Digiteal")
    const bancontactEnabled =
        !merchant.isDeletionPending &&
        swiipePlanSelected &&
        !!bancontactOnboardingStatus &&
        bancontactOnboardingStatus !== "ActionRequired"

    const bancontact: IPlugin = {
        id: "Bancontact",
        platforms: getAvailablePlatformsForPaymentType("Bancontact"),
        price: getTextWithInvoicingPrices("plugins.bancontactPrice", global.invoicingPrices, {
            bancontactTx: { type: "BancontactTransactionShared" },
        }),
        editNode: bancontactEnabled ? (
            <div>
                <span onClick={() => navigationService.navigate("/bancontact_edit")}>{t("plugins.editNode.seeDetails")}</span>
            </div>
        ) : undefined,
        priceSubText: t("plugins.plusPspFees"),
        logo: getImage("bancontactLogo"),
        isFetched: webshop.providers && merchant.gateways ? true : false,
        isOnboarded: bancontactEnabled,
        logoWidth: 135,
        title: "",
        buttonLink: "/bancontact_onboard",
        pluginDescription: t("plugins.bancontactDescription"),
        legalTextType: ["TermsBancontact"],
        legalTextLinkText: t("plugins.bancontactLegalText"),
        legalTextLinkOnboarded: [t("serviceNames.bancontact")],
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    const idealOnboardingStatus = getProviderStatus(webshop.providers?.gatewayProviders, "Ideal", "Digiteal")
    const idealEnabled =
        !merchant.isDeletionPending && swiipePlanSelected && !!idealOnboardingStatus && idealOnboardingStatus !== "ActionRequired"

    const ideal: IPlugin = {
        id: "Ideal",
        platforms: getAvailablePlatformsForPaymentType("Ideal"),
        price: getTextWithInvoicingPrices("plugins.idealPrice", global.invoicingPrices, {
            idealTx: { type: "IdealTransactionShared" },
        }),
        editNode: idealEnabled ? (
            <div>
                <span onClick={() => navigationService.navigate("/ideal_edit")}>{t("plugins.editNode.seeDetails")}</span>
            </div>
        ) : undefined,
        priceSubText: !isOnboardedForCardPayments ? t("plugins.plusPspFees") : undefined,
        logo: getImage("idealLogo"),
        isFetched: webshop.providers && merchant.gateways ? true : false,
        isOnboarded: idealEnabled,
        logoWidth: 135,
        title: "",
        buttonLink: "/ideal_onboard",
        pluginDescription: t("plugins.idealDescription", {
            linkStart: `<a href='https://ideal.nl/en/wat-is-ideal' target='_blank' rel="noopener noreferrer">`,
            linkEnd: "</a>",
        }),
        legalTextType: ["TermsIdeal"],
        legalTextLinkText: t("plugins.idealLegalText"),
        legalTextLinkOnboarded: [t("serviceNames.ideal")],
        onboardedText: t("plugins.onboarded"),
        priceHasLotsOfText: true,
        priceAlignCenter: true,
        canEdit: false,
    }

    return filterPluginsByPlatform(
        [
            swiipePayments,
            viabill,
            anyday,
            bancontact,
            ideal,
            dankort,
            forbrugsforeningen,
            mobilePayOnline,
            swish,
            vipps,
            klarna,
            resurs,
            termsTech,
            winBack,
            plusSell,
            // anywhereMobile,
            returnSell,
            global.env === "Sandbox" ? undefined : applePay, // Disabled until we can do apple pay on sandbox - currently we cannot ensure valid merchant url at apple
            // accountToAccount,
        ].filter(hasValue),
        webshop.details.platform
    )
}

interface IMerchantPluginsPageProps {}
const MerchantPluginsPage = ({}: IMerchantPluginsPageProps) => {
    const dispatch = useReduxDispatch()
    useRequiredUserRelations([{ requiredType: "Merchant" }])

    const currentUserRelation = useSelector(userRelationSelectors.currentUserRelation)
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const preOnboardingDetails = useMerchantPreOnboardingDetails()

    const selectedWebshopId = useSelector(merchantSelectors.selectedWebshopId)

    useEffect(() => {
        dispatch(getMerchantPreOnboardingDetailsThunk())
    }, [currentUserRelation?.id])

    if (!merchantDetails || !merchantDetails.webshops.length || !preOnboardingDetails || !selectedWebshopId) {
        return null
    }

    return (
        <MerchantPluginsPageInner
            merchantDetails={merchantDetails}
            selectedWebshopId={selectedWebshopId}
            preOnboardingDetails={preOnboardingDetails?.exists ? preOnboardingDetails : undefined}
        ></MerchantPluginsPageInner>
    )
}

const highlightedServicesPerCountry: { [countryCode: string]: TSwiipeService[] } = {
    DK: ["Winback", "CreditCard", "MobilePayOnline", "PlusSell", "ReturnSell"],
    BE: ["Winback", "CreditCard", "Bancontact", "Ideal", "TermsTech", "ReturnSell"],
    NL: ["Winback", "CreditCard", "Bancontact", "Ideal", "TermsTech", "ReturnSell"],
    LU: ["Winback", "CreditCard", "Bancontact", "Ideal", "TermsTech", "ReturnSell"],
    SE: ["Winback", "CreditCard", "Swish", "KlarnaPayLater", "KlarnaFinancing", "PlusSell", "ReturnSell"],
    NO: ["Winback", "CreditCard", "Vipps", "PlusSell", "ReturnSell"],
}
const highlightedServices: TSwiipeService[] = ["PlusSell", "Winback", "CreditCard", "ReturnSell"]

interface IMerchantPluginsPageInnerProps {
    merchantDetails: MixedMerchantDetails
    selectedWebshopId: string
    preOnboardingDetails: IMerchantPreOnboardingDetails | undefined
}
const MerchantPluginsPageInner = ({
    merchantDetails,
    preOnboardingDetails,
    selectedWebshopId,
}: IMerchantPluginsPageInnerProps) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()

    const env = useSelector(sharedConfigurationSelectors.environment)

    const shopifyAppUrls = useSelector((state: StoreState) =>
        shopifySelectors.appUrls(state, merchantDetails.swMerchant.swMerchantId)
    )
    const shopifyAppUrlsForWebshop = shopifyAppUrls?.[selectedWebshopId] ?? []

    const webshop = merchantDetails.webshops.find((w) => w.webshopId === selectedWebshopId)
    const platform = webshop?.platform

    const configuredProviders = useConfiguredPaymentProviders(selectedWebshopId)

    const calculatedDeps = useCalculatedPluginsDependencies(merchantDetails, selectedWebshopId)

    const pluginData = getPluginData(calculatedDeps.webshop.details, calculatedDeps.merchant.country, env)
    const serviceData = useServiceData(
        calculatedDeps.webshop,
        calculatedDeps.merchant,
        calculatedDeps.global,
        calculatedDeps.clearhaus,
        calculatedDeps.sandbox
    )
    const countryCode = merchantDetails.swMerchant.countryCode
    const serviceDataForCountry = serviceData.filter((sd) => !sd.id || isServiceAvailableForMerchantCountry(sd.id, countryCode))

    const termsStatuses = useSelector(legalSelectors.termsStatuses)
    useEffect(() => {
        dispatch(ensureTermsStatusThunk(false))
        if (merchantDetails.webshops.find((w) => w.platform === "shopify")) {
            dispatch(ensureShopifyAppUrlsThunks(merchantDetails.swMerchant.swMerchantId, false))
        }
    }, [merchantDetails])

    const preOnboardedServices = mapMerchantOnboardingServicesSubFormToServices(preOnboardingDetails?.services)
    const yourServices = serviceDataForCountry.filter((s) => (s.id && preOnboardedServices.includes(s.id)) || s.isOnboarded)
    const recommendServices = serviceDataForCountry.filter(
        (s) =>
            s.id &&
            !yourServices.find((yourService) => yourService.id === s.id) &&
            (highlightedServicesPerCountry[countryCode]
                ? highlightedServicesPerCountry[countryCode].includes(s.id)
                : highlightedServices.includes(s.id))
    )
    const otherServices = serviceDataForCountry.filter(
        (s) =>
            s.id &&
            !yourServices.find((yourService) => yourService.id === s.id) &&
            !recommendServices.find((yourService) => yourService.id === s.id)
    )
    const activePayFac = getActivePayFac(countryCode)

    const renderPlugin = (plugin: IPlugin, disableSeparator: boolean) => {
        const terms = !plugin.id ? [] : getTermsForSwiipeService(plugin.id, activePayFac, configuredProviders)
        const foundTermsStatuses = terms.map((t) => termsStatuses?.find((ts) => ts.termsType === t))
        const isMissingTerms =
            !!termsStatuses && foundTermsStatuses.length > 0 && foundTermsStatuses.filter((ts) => !hasValue(ts)).length > 0
        const foundNotAcceptedTermsStatuses = foundTermsStatuses.filter(hasValue).find((ts) => !ts.isAnyAccepted)

        if (!webshop) {
            return null
        }

        return (
            <Plugins
                key={plugin.id ?? ""}
                merchantDetails={merchantDetails}
                gateways={calculatedDeps.merchant.gateways}
                {...plugin}
                isTermsAccepted={
                    !termsStatuses || isMissingTerms || terms.length === 0 ? undefined : !foundNotAcceptedTermsStatuses
                }
                disableSeparator={disableSeparator}
                webshop={webshop}
            />
        )
    }

    return (
        <>
            <PageWrapper disableMinHeight>
                <PageContainer>
                    <WebshopSelect
                        merchantDetails={merchantDetails}
                        actionLink={
                            platform === "shopify" && shopifyAppUrlsForWebshop.length > 0
                                ? {
                                      title: t("shopify.goToSettings"),
                                      href: getBestShopifyAppUrls(shopifyAppUrlsForWebshop, env)?.settingsUrl ?? "",
                                      target: "_blank",
                                  }
                                : undefined
                        }
                    />
                </PageContainer>
            </PageWrapper>
            <PageWrapper>
                {(pluginData.length > 0 || yourServices.length > 0) && (
                    <PageContainer id="plugins-top" title={t("plugins.plugins")}>
                        <div className="services-title">{t("plugins.yourServices")}</div>
                        {pluginData.map((pData) => renderPlugin(pData, false))}
                        {yourServices.map((pData) => renderPlugin(pData, false))}
                    </PageContainer>
                )}

                {recommendServices.length > 0 && (
                    <PageContainer id="plugins-middle">
                        <div className="services-title">{t("plugins.recommendedServices")}</div>
                        {recommendServices.map((pData, index) => renderPlugin(pData, index == 0))}
                    </PageContainer>
                )}

                {otherServices.length > 0 && (
                    <AccordionWithChildren header={t("plugins.otherServices").toUpperCase()}>
                        <PageContainer id="plugins-bottom" noHeader disableDefaultPaddingBottom>
                            {otherServices.map((pData, index) => renderPlugin(pData, index == 0))}
                        </PageContainer>
                    </AccordionWithChildren>
                )}
            </PageWrapper>
        </>
    )
}

export default MerchantPluginsPage
